import _ from 'lodash';
import React from 'react';
import { Report, ReportConfig } from './types/CohortReport';

const ALL = "all"
const ELECTIVE = "elective"
const NON_ELECTIVE = "non-elective"

const FIRST = "first"
const FOLLOW_UP = "follow-up"


export interface CellData {
    rowSpan: number;
    colSpan: number;
    className: string;
    label: string;
}

const renderCell = (content: CellData | string, row: any, rowIndex: number) => {
    if (_.isObject(content)) {
        return {
            children: React.createElement("span", { className: content.className }, content.label),
            props: {
                rowSpan: content.rowSpan,
                colSpan: content.colSpan
            },
        };
    } else {
        return {
            children: content
        };
    }
}

const headerColumn = {
    id: '1',
    title: '',
    children: [
        {
            id: '11',
            title: 'Metrics',
            children: [
                {
                    id: '111',
                    title: '',
                    dataIndex: '0',
                    key: 'label',
                    width: 50,
                    fixed: 'left',
                    render: renderCell,
                    // props for each cells in that column
                    onCell: (record: any, rowIndex: number) => ({
                        className: 'header-0',
                    })
                },
                {
                    title: '',
                    dataIndex: '1',
                    width: 50,
                    fixed: 'left',
                    render: renderCell,
                    // props for each cells in that column
                    onCell: (record: any, rowIndex: number) => ({
                        className: 'header-1',
                    })
                },
                {
                    title: '',
                    dataIndex: '2',
                    width: 50,
                    fixed: 'left',
                    render: renderCell,
                    // props for each cells in that column
                    onCell: (record: any, rowIndex: number) => ({
                        className: 'header-2',
                    })
                },
                {
                    title: '',
                    dataIndex: '3',
                    width: 150,
                    fixed: 'left',
                    render: renderCell,
                    // props for each cells in that column
                    onCell: (record: any, rowIndex: number) => ({
                        className: 'header-3',
                    })
                }
            ]
        }
    ]
}

function initialHeaders() {
    return [
        headerColumn
    ]
}

const emptyRawData = () => {
    const metricsTemplate = {
        avg: [],
        'percentile-0.50': [],
        min: [],
        max: [],
        iqr: [],
        stddev: [],
    }
    const ipMetricsGroupTemplate = {
        all: metricsTemplate,
        elective: metricsTemplate,
        'non-elective': metricsTemplate,
    }
    const opMetricsGroupTemplate = {
        all: metricsTemplate,
        first: metricsTemplate,
        'follow-up': metricsTemplate,
    }
    const aandeMetricsGroupTemplate = {
        all: metricsTemplate,
    }
    return {
        ip: {
            patients: {
                all: { count: [] },
                'non-elective': { count: [] },
                elective: { count: [] }
            },
            admissions: {
                all: { count: [] },
                'non-elective': { count: [] },
                elective: { count: [] }
            },
            pppy: {
                admissions: ipMetricsGroupTemplate,
                cost: ipMetricsGroupTemplate,
                los: ipMetricsGroupTemplate,
            },
            pp: {
                admissions: ipMetricsGroupTemplate,
                cost: ipMetricsGroupTemplate,
                los: ipMetricsGroupTemplate,
            },
            pe: {
                cost: ipMetricsGroupTemplate,
                los: ipMetricsGroupTemplate,
            }
        },
        op: {
            patients: {
                all: { count: [] },
                first: { count: [] },
                'follow-up': { count: [] }
            },
            appointments: {
                all: { count: [] },
                first: { count: [] },
                'follow-up': { count: [] }
            },
            pppy: {
                appointments: opMetricsGroupTemplate,
                cost: opMetricsGroupTemplate
            },
            pp: {
                appointments: opMetricsGroupTemplate,
                cost: opMetricsGroupTemplate
            },
            pe: {
                cost: opMetricsGroupTemplate
            },
        },
        aande: {
            patients: {
                all: { count: [] }
            },
            attendances: {
                all: { count: [] }
            },
            pppy: {
                attendances: aandeMetricsGroupTemplate
            },
            pp: {
                attendances: aandeMetricsGroupTemplate
            }
        },
    }
}

const transformArrayDataToObjectData = (arr: string[]) => {
    const result: Record<string, string> = {}
    arr.forEach((data: any, index) => {
        result[`data-${index}`] = data
    })
    return result
}

interface ReportData {
    [key: string]: string[];
}

// const extractRowData = (data: Map<string, object[]>) => {
const extractRowData = (data: ReportData) => {
    return [
        {
            ...transformArrayDataToObjectData(data['avg'])
        },
        {
            ...transformArrayDataToObjectData(data['percentile-0.50'])
        },
        {
            ...transformArrayDataToObjectData(data['min'])
        },
        {
            ...transformArrayDataToObjectData(data['max'])
        },
        {
            ...transformArrayDataToObjectData(data['iqr'])
        },
        {
            ...transformArrayDataToObjectData(data['stddev'])
        }
    ]
}

const metricsRowTemplate = [
    {
        '0': { rowSpan: 0, colSpan: 0 },
        '1': { rowSpan: 0, colSpan: 0 },
        '2': { rowSpan: 0, colSpan: 0 },
        '3': { label: 'Mean' }
    },
    {
        '0': { rowSpan: 0, colSpan: 0 },
        '1': { rowSpan: 0, colSpan: 0 },
        '2': { rowSpan: 0, colSpan: 0 },
        '3': { label: 'Median' }
    },
    {
        '0': { rowSpan: 0, colSpan: 0 },
        '1': { rowSpan: 0, colSpan: 0 },
        '2': { rowSpan: 0, colSpan: 0 },
        '3': { label: 'Minimum' }
    },
    {
        '0': { rowSpan: 0, colSpan: 0 },
        '1': { rowSpan: 0, colSpan: 0 },
        '2': { rowSpan: 0, colSpan: 0 },
        '3': { label: 'Maximum' }
    },
    {
        '0': { rowSpan: 0, colSpan: 0 },
        '1': { rowSpan: 0, colSpan: 0 },
        '2': { rowSpan: 0, colSpan: 0 },
        '3': { label: 'Interquartile Ranges' }
    },
    {
        '0': { rowSpan: 0, colSpan: 0 },
        '1': { rowSpan: 0, colSpan: 0 },
        '2': { rowSpan: 0, colSpan: 0 },
        '3': { label: 'Standard Deviations' }
    }
]

function zipArrays(a: any, b: any) {
    return a.map((e: any, i: number) => {
        return Object.assign({}, e, b[i])
    })
}

const getData = (data: any) => {

    const removeHeaders = (i: any) => {
        delete i["0"]
        delete i["1"]
        delete i["2"]
        delete i["3"]
        return i
    }

    const rowData = extractRowData(data)
    const mergedData = zipArrays(metricsRowTemplate, rowData)

    removeHeaders(mergedData[0])

    return mergedData
}

export function placeHolderData(report: Report) {
    let rawData = emptyRawData()

    if (!_.isNull(report.sparkQuery) && 'FINISHED' === report.sparkQuery.status) {
        rawData = JSON.parse(report.sparkQuery.response)
    }

    const rows = {
        ip: report.reportConfig.ip ? ipData(report, rawData) : [],
        op: report.reportConfig.op ? opData(report, rawData) : [],
        aande: report.reportConfig.aande ? aandeData(report, rawData) : []
    }

    return rows
}

const addIndex = (arr: any[]) => {
    return arr.map((d, i) => {
        return Object.assign(d, { index: i })
    })
}

const ipData = (report: Report, rawData: any) => {
    const empty = { rowSpan: 0, colSpan: 0 }

    const defaultRows = [
        {
            '0': { label: 'Patient Count', rowSpan: 1, colSpan: 4, className: 'horizontal' },
            '1': empty,
            '2': empty,
            '3': empty,
            ...transformArrayDataToObjectData(rawData.ip.patients.all.count)
        },
        {
            '0': { label: 'Total Admissions', rowSpan: 3, colSpan: 1 },
            '1': { label: 'All', rowSpan: 1, colSpan: 3 },
            '2': empty,
            '3': empty,
            ...transformArrayDataToObjectData(rawData.ip.admissions.all.count)
        },
        {
            '0': { label: 'Total Admissions', rowSpan: 0, colSpan: 1 },
            '1': { label: 'Elective', rowSpan: 1, colSpan: 3 },
            '2': empty,
            '3': empty,
            ...transformArrayDataToObjectData(rawData.ip.admissions.elective.count)
        },
        {
            '0': { label: 'Total Admissions', rowSpan: 0, colSpan: 1 },
            '1': { label: 'Non-Elective', rowSpan: 1, colSpan: 3 },
            '2': empty,
            '3': empty,
            ...transformArrayDataToObjectData(rawData.ip.admissions[NON_ELECTIVE].count)
        },
    ]

    let rows: any = []

    if (report.reportConfig.showPerPatientsPerYear) {
        rows = rows.concat(ipDataForEachMetric(false, 'Per Patient Per Year', rawData.ip.pppy))
    }

    if (report.reportConfig.showPerPatient) {
        rows = rows.concat(ipDataForEachMetric(false, 'Per Patient', rawData.ip.pp))
    }

    if (report.reportConfig.showPerEvent) {
        rows = rows.concat(ipDataForEachMetric(true, 'Per Event', rawData.ip.pe))
    }

    return addIndex([...defaultRows, ...rows])
}

const ipDataForEachMetric = (perEvent: boolean, title: string, metricData: any) => {
    let admissions = []

    if (!perEvent) {
        admissions = ipDataTemplate(
            title,
            'Admissions',
            {
                [ALL]: getData(metricData.admissions[ALL]),
                [ELECTIVE]: getData(metricData.admissions[ELECTIVE]),
                [NON_ELECTIVE]: getData(metricData.admissions[NON_ELECTIVE])
            }
        )
    }

    const costs = ipDataTemplate(
        title,
        'Costs',
        {
            [ALL]: getData(metricData.cost[ALL]),
            [ELECTIVE]: getData(metricData.cost[ELECTIVE]),
            [NON_ELECTIVE]: getData(metricData.cost[NON_ELECTIVE])
        }
    )

    const los = ipDataTemplate(
        title,
        'Length of Stay',
        {
            [ALL]: getData(metricData.los[ALL]),
            [ELECTIVE]: getData(metricData.los[ELECTIVE]),
            [NON_ELECTIVE]: getData(metricData.los[NON_ELECTIVE])
        }
    )

    const rows = [
        ...admissions,
        ...costs,
        ...los
    ]

    return rows
}

const ipDataTemplate = (title: string, subTitle: string, data: any) => {
    const numberOfMetrics = metricsRowTemplate.length

    return [
        {
            '0': { label: title, rowSpan: numberOfMetrics * 3, colSpan: 1 },
            '1': { label: subTitle, rowSpan: numberOfMetrics * 3, colSpan: 1, className: 'vertical' },
            '2': { label: 'All', rowSpan: numberOfMetrics, colSpan: 1, className: 'vertical' },
            '3': metricsRowTemplate[0]['3'], // get the mean
            ...data[ALL][0]
        },
        ...data[ALL].slice(1),
        {
            '0': { rowSpan: 0, colSpan: 1 },
            '1': { label: subTitle, rowSpan: 0, colSpan: 1, className: 'vertical' },
            '2': { label: 'Elective', rowSpan: numberOfMetrics, colSpan: 1, className: 'vertical' },
            '3': metricsRowTemplate[0]['3'], // get the mean
            ...data[ELECTIVE][0]
        },
        ...data[ELECTIVE].slice(1),
        {
            '0': { rowSpan: 0, colSpan: 1 },
            '1': { label: subTitle, rowSpan: 0, colSpan: 1, className: 'vertical' },
            '2': { label: 'Non-Elective', rowSpan: numberOfMetrics, colSpan: 1, className: 'vertical' },
            '3': metricsRowTemplate[0]['3'], // get the mean
            ...data[NON_ELECTIVE][0]
        },
        ...data[NON_ELECTIVE].slice(1),
    ]
}

const opData = (report: Report, rawData: any) => {
    const empty = { rowSpan: 0, colSpan: 0 }

    const defaultRows = [
        {
            '0': { label: 'Patient Count', rowSpan: 1, colSpan: 4, className: 'horizontal' },
            '1': empty,
            '2': empty,
            '3': empty,
            ...transformArrayDataToObjectData(rawData.op.patients.all.count)
        },
        {
            '0': { label: 'Total Appointments', rowSpan: 3, colSpan: 1 },
            '1': { label: 'All', rowSpan: 1, colSpan: 3 },
            '2': empty,
            '3': empty,
            ...transformArrayDataToObjectData(rawData.op.appointments.all.count)
        },
        {
            '0': { label: 'Total Appointments', rowSpan: 0, colSpan: 1 },
            '1': { label: 'First', rowSpan: 1, colSpan: 3 },
            '2': empty,
            '3': empty,
            ...transformArrayDataToObjectData(rawData.op.appointments.first.count)
        },
        {
            '0': { label: 'Total Appointments', rowSpan: 0, colSpan: 1 },
            '1': { label: 'Follow-up', rowSpan: 1, colSpan: 3 },
            '2': empty,
            '3': empty,
            ...transformArrayDataToObjectData(rawData.op.appointments[FOLLOW_UP].count)
        }
    ]

    let rows: any = []

    if (report.reportConfig.showPerPatientsPerYear) {
        rows = rows.concat(opDataForEachMetric(false, 'Per Patient Per Year', rawData.op.pppy))
    }

    if (report.reportConfig.showPerPatient) {
        rows = rows.concat(opDataForEachMetric(false, 'Per Patient', rawData.op.pp))
    }

    if (report.reportConfig.showPerEvent) {
        rows = rows.concat(opDataForEachMetric(true, 'Per Event', rawData.op.pe))
    }

    return addIndex([...defaultRows, ...rows])
}

const opDataForEachMetric = (perEvent: boolean, title: string, metricData: any) => {
    let appointments: any = []

    if (!perEvent) {
        opDataTemplate(
            title,
            'Appointments',
            {
                [ALL]: getData(metricData.appointments[ALL]),
                [FIRST]: getData(metricData.appointments[FIRST]),
                [FOLLOW_UP]: getData(metricData.appointments[FOLLOW_UP])
            }
        )
    }

    const costs = opDataTemplate(
        title,
        'HRG Tariffs',
        {
            [ALL]: getData(metricData.cost[ALL]),
            [FIRST]: getData(metricData.cost[FIRST]),
            [FOLLOW_UP]: getData(metricData.cost[FOLLOW_UP])
        }
    )

    const rows = [
        ...appointments,
        ...costs
    ]

    return rows
}

const opDataTemplate = (title: string, subTitle: string, data: any) => {
    const numberOfMetrics = metricsRowTemplate.length

    return [
        {
            '0': { label: title, rowSpan: numberOfMetrics * 3, colSpan: 1 },
            '1': { label: subTitle, rowSpan: numberOfMetrics * 3, colSpan: 1, className: 'vertical' },
            '2': { label: 'All', rowSpan: numberOfMetrics, colSpan: 1, className: 'vertical' },
            '3': metricsRowTemplate[0]['3'], // get the mean
            ...data[ALL][0]
        },
        ...data[ALL].slice(1),
        {
            '0': { rowSpan: 0, colSpan: 1 },
            '1': { label: subTitle, rowSpan: 0, colSpan: 1, className: 'vertical' },
            '2': { label: 'First', rowSpan: numberOfMetrics, colSpan: 1, className: 'vertical' },
            '3': metricsRowTemplate[0]['3'], // get the mean
            ...data[FIRST][0]
        },
        ...data[FIRST].slice(1),
        {
            '0': { rowSpan: 0, colSpan: 1 },
            '1': { label: subTitle, rowSpan: 0, colSpan: 1, className: 'vertical' },
            '2': { label: 'Follow-up', rowSpan: numberOfMetrics, colSpan: 1, className: 'vertical' },
            '3': metricsRowTemplate[0]['3'], // get the mean
            ...data[FOLLOW_UP][0]
        },
        ...data[FOLLOW_UP].slice(1),
    ]
}

const aandeData = (report: Report, rawData: any) => {
    const empty = { rowSpan: 0, colSpan: 0 }

    const defaultRows = [
        {
            '0': { label: 'Patient Count', rowSpan: 1, colSpan: 4, className: 'horizontal' },
            '1': empty,
            '2': empty,
            '3': empty,
            ...transformArrayDataToObjectData(rawData.aande.patients.all.count)
        },
        {
            '0': { label: 'Total Attendances', rowSpan: 1, colSpan: 4, className: 'horizontal' },
            '1': empty,
            '2': empty,
            '3': empty,
            ...transformArrayDataToObjectData(rawData.aande.attendances.all.count)
        }
    ]

    let rows: any = []

    if (report.reportConfig.showPerPatientsPerYear) {
        rows = rows.concat(aandeDataForEachMetric('Per Patient Per Year', rawData.aande.pppy))
    }

    if (report.reportConfig.showPerPatient) {
        rows = rows.concat(aandeDataForEachMetric('Per Patient', rawData.aande.pp))
    }

    return addIndex([...defaultRows, ...rows])
}

const aandeDataForEachMetric = (title: string, metricData: any) => {
    const attendances = aandeDataTemplate(
        title,
        'Attendances',
        {
            [ALL]: getData(metricData.attendances[ALL])
        }
    )

    const rows = [
        ...attendances
    ]

    return rows
}

const aandeDataTemplate = (title: string, subTitle: string, data: any) => {
    const numberOfMetrics = metricsRowTemplate.length

    return [
        {
            '0': { label: title, rowSpan: numberOfMetrics * 1, colSpan: 1 },
            '1': { label: subTitle, rowSpan: numberOfMetrics * 1, colSpan: 1, className: 'vertical' },
            '2': { label: 'All', rowSpan: numberOfMetrics, colSpan: 1, className: 'vertical' },
            '3': metricsRowTemplate[0]['3'], // get the mean
            ...data[ALL][0]
        },
        ...data[ALL].slice(1)
    ]
}

export function remove<T>(array: Array<T>, value: T) {
    const index = array.indexOf(value)
    array.splice(index, 1)
}

const cleanup = (col: any) => {
    if (!_.isNil(col.children)) {
        if (col.children.length === 0) {
            delete col.children
        } else {
            col.children.forEach((i: any) => cleanup(i))
        }
    }
}

const calculateColumnWidth = (showTotals: boolean, periodsLength: number, group1Length: number, group2Length: number, group3Length: number) => {
    const totals = (showTotals ? 1 : 0)
    const totalColumns = periodsLength * (group1Length + totals) * (group2Length + totals) * (group3Length + totals)

    let lowestLevel = "0"
    if (group3Length > 0) {
        lowestLevel = "3"
    } else if (group2Length > 0) {
        lowestLevel = "2"
    } else if (group1Length > 0) {
        lowestLevel = "1"
    }

    if (totalColumns >= 8) {
        return {
            level: lowestLevel,
        }
    } else {
        return {}
    }
}

export function calculateHeaders(config: ReportConfig, onEditPeriod: any, onEditGroup: any, amendingGroup: any) {
    const { showTotals, periods, group1, group2, group3 } = config
    const newHeaders = _.cloneDeep(initialHeaders())

    const widthConfig = calculateColumnWidth(showTotals, periods.length, group1.length, group2.length, group3.length)

    // periods headers
    periods.forEach(i => {
        const id: string = i.label
        newHeaders.push({
            id: id,
            // @ts-ignore
            key: id,
            raw: i,
            title: i.label,
            dataIndex: 'all',
            className: 'index-period-cell ' + i.value,
            onHeaderCell: (record: any, rowIndex: number) => ({
                onClick: () => {
                    if (record.raw.value !== 'all') {
                        onEditPeriod(record.raw)
                    }
                }
            }),
            children: [],
        })
    })

    // group 1 headers
    const dynamicHeaders = newHeaders.slice(1)

    let index_g1 = 0
    dynamicHeaders.forEach(header => {
        defaultAllHeader("1", showTotals).concat(group1).forEach(g1 => {
            const key = `${header.id}-${g1.id}`
            header.children.push(createChild(index_g1, widthConfig, key, g1, onEditGroup, amendingGroup))
            index_g1++
        })
    })

    // group 2 headers
    let index_g2 = 0
    dynamicHeaders.forEach(header => {
        header.children.forEach(g1 => {
            if (group2.length > 0 || group3.length > 0) {
                defaultAllHeader("2", showTotals).concat(group2).forEach(g2 => {
                    const key = `${header.id}-${g1.id}-${g2.id}`
                    g1.children.push(createChild(index_g2, widthConfig, key, g2, onEditGroup, amendingGroup))
                    index_g2++
                })
            }
        })
    })

    // group 3 headers
    let index_g3 = 0
    dynamicHeaders.forEach(header => {
        header.children.forEach(g1 => {
            g1.children.forEach(g2 => {
                if (group3.length > 0) {
                    defaultAllHeader("3", showTotals).concat(group3).forEach(g3 => {
                        const key = `${header.id}-${g1.id}-${g2.id}-${g3.id}`
                        // @ts-ignore
                        g2.children.push(createChild(index_g3, widthConfig, key, g3, onEditGroup, amendingGroup))
                        index_g3++
                    })
                }
            })
        })
    })

    // null children if empty
    dynamicHeaders.forEach(i => cleanup(i))

    return newHeaders
}

function defaultAllHeader(level: string, showTotals: boolean): any[] {
    if (showTotals) {
        return [
            {
                id: `all-${level}`,
                title: 'Total',
                value: 'all',
                level: level
            }
        ]
    } else {
        return []
    }
}

function createChild(index: number, widthConfig: any, key: any, group: any, onEditGroup: any, amendingGroup: any) {
    const extraConfig: any = {}
    if (group.level === widthConfig.level) {
        extraConfig.width = 100
    }

    const editing = !_.isNil(amendingGroup) && amendingGroup.id === group.id

    return {
        id: group.id,
        key: key,
        title: group.title,
        raw: group,
        onHeaderCell: (record: any, rowIndex: number) => ({
            onClick: () => {
                if (record.raw.value !== 'all') {
                    onEditGroup(record.raw)
                }
            }
        }),
        dataIndex: `data-${index}`,
        className: group.value === 'all' ? 'all-cell' : `group${group.level}-cell` + (editing ? ' editing ' : ''),
        children: [],
        ...extraConfig
    }
}